import * as Sentry from '@sentry/react'
import { RouteNames } from 'RouteNames'
import { COMMIT_LOGIN_EVENTS_MAP, COMMIT_ONBOARDING_EVENTS_MAP, COMMIT_USC_EVENTS_MAP, COMMIT_WELLHUB_EVENTS_MAP, GO_BACK_TO_LOGIN_EVENTS_MAP, GO_BACK_TO_ONBOARDING_EVENTS_MAP, GO_TO_LOGIN_EVENTS_MAP, GO_TO_ONBOARDING_EVENTS_MAP, GO_TO_USC_EVENTS_MAP, GO_TO_WELLHUB_EVENTS_MAP, SKIP_ONBOARDING_EVENTS_MAP, STATIC_ROUTE_MAP } from './Constants'
import invariant from 'invariant'

export enum Flows {
  ONBOARDING = 'onboarding',
  WELLHUB = 'wellhub',
  USC = 'usc',
  LOGIN = 'login',
  STATIC = 'static',
  MANAGE_SUBSCRIPTION = 'manage_subscription'
}

const STATIC_ROUTE_NAMES: string[] = [
  RouteNames.PRIVACY,
  RouteNames.COOKIE_POLICY,
  RouteNames.TERMS_OF_USE
]

const LOGIN_ROUTE_NAMES: string[] = [
  RouteNames.LOGIN,
  RouteNames.LOGIN_TOKEN,
  RouteNames.LOGIN_PAYMENT,
  RouteNames.LOGIN_PAYMENT_SUCCESS,
  RouteNames.LOGIN_UPDATE_SUBSCRIPTION,
  RouteNames.LOGIN_UPGRADE_ACCOUNT, // Users still can see the upgrade account screen for futoken logins
  RouteNames.LOGIN_CREATE_ACCOUNT,
  RouteNames.LOGIN_DOWNLOAD
]

const ONBOARDING_ROUTE_NAMES: string[] = [
  RouteNames.HOME,
  RouteNames.ONBOARDING,
  RouteNames.PAYMENT,
  RouteNames.EBOOK_PURCHASE,
  RouteNames.EBOOK_PURCHASE_SUCCESS,
  RouteNames.UPGRADE_ACCOUNT,
  RouteNames.DOWNLOAD,
  RouteNames.PAYMENT_SUCCESS,
  RouteNames.UPDATE_SUBSCRIPTION
]

const USC_ROUTE_NAMES: string[] = [
  RouteNames.USC,
  RouteNames.USC_REDEEM,
  RouteNames.USC_ACCOUNT,
  RouteNames.USC_DOWNLOAD
]

const WELLHUB_ROUTE_NAMES: string[] = [
  RouteNames.WELLHUB,
  RouteNames.WELLHUB_LOGIN,
  RouteNames.WELLHUB_REGISTER,
  RouteNames.WELLHUB_WELCOME_BACK,
  RouteNames.WELLHUB_DOWNLOAD
]

export const getCurrentFlow = (): Flows => {
  const route = `/${location.pathname.split('/')[1]}`
  if (LOGIN_ROUTE_NAMES.includes(route)) {
    return Flows.LOGIN
  } else if (ONBOARDING_ROUTE_NAMES.includes(route)) {
    return Flows.ONBOARDING
  } else if (WELLHUB_ROUTE_NAMES.includes((route))) {
    return Flows.WELLHUB
  } else if (USC_ROUTE_NAMES.includes(route)) {
    return Flows.USC
  } else if (STATIC_ROUTE_NAMES.includes(route)) {
    return Flows.STATIC
  } else if (route.includes(RouteNames.MANAGE_SUBSCRIPTION)) {
    return Flows.MANAGE_SUBSCRIPTION
  } else {
    Sentry.captureException('Unknown flow to path: ' + location.pathname)
    logEvent('web_error_unknown_flow_path', { path: location.pathname })
    return Flows.ONBOARDING
  }
}

// getGoToEventsMap, getCommitEventsMap, getGoBackToEventsMap have the possibility to be called by STATIC flow which shouldn't trigger any events.
// We added a check that returns null in that case, thus the added null return type.

export const getGoToEventsMap = (flow: Flows, eventKey: string): string => {
  invariant(flow !== Flows.STATIC, 'getGoToEventsMap should not be called with STATIC flow')
  return flowToGoToEventsMap[flow][eventKey]
}

export const getCommitEventsMap = (flow: Flows, eventKey: string): string => {
  invariant(flow !== Flows.STATIC, 'getCommitEventsMap should not be called with STATIC flow')
  return flowToCommitEventsMap[flow][eventKey]
}

export const getGoBackToEventsMap = (flow: Flows, eventKey: string): string => {
  invariant(flow !== Flows.STATIC, 'getGoBackToEventsMap should not be called with STATIC flow')
  return flowGoBackToEventsMap[flow][eventKey]
}

export const getSkipEventsMap = (): { [key: string]: string } => {
  const flow = getCurrentFlow()
  return flowToSkipEventsMap[flow]
}

export const getPaymentUrl = (): string => {
  const flow = getCurrentFlow()
  return flowToPaymentUrlMap[flow]
}

export const getPaymentSuccessUrl = (): string => {
  const flow = getCurrentFlow()
  return flowToPaymentSuccessUrlMap[flow]
}

export const getUpdateSubscriptionUrl = (): string => {
  const flow = getCurrentFlow()
  return flowToUpdateSubscription[flow]
}

export const getEbookPurchaseUrl = (): string => {
  const flow = getCurrentFlow()
  return flowToEbookPurchaseUrlMap[flow]
}

export const getEbookPurchaseSuccessUrl = (): string => {
  const flow = getCurrentFlow()
  return flowToEbookPurchaseSuccessUrlMap[flow]
}

export const getUpgradeAccountUrl = (): string => {
  const flow = getCurrentFlow()
  return flowToUpgradeAccountUrlMap[flow]
}

export const getDownloadUrl = () => {
  const flow = getCurrentFlow()
  return flowToDownloadUrlMap[flow]
}

const flowToPaymentUrlMap: { [key: string]: string } = {
  [Flows.ONBOARDING]: RouteNames.PAYMENT,
  [Flows.LOGIN]: RouteNames.LOGIN_PAYMENT,
  [Flows.MANAGE_SUBSCRIPTION]: RouteNames.MANAGE_SUBSCRIPTION_PAYMENT
}

const flowToPaymentSuccessUrlMap: { [key: string]: string } = {
  [Flows.ONBOARDING]: RouteNames.PAYMENT_SUCCESS,
  [Flows.LOGIN]: RouteNames.LOGIN_PAYMENT_SUCCESS,
  [Flows.MANAGE_SUBSCRIPTION]: RouteNames.MANAGE_SUBSCRIPTION_PAYMENT_SUCCESS
}

const flowToUpdateSubscription: { [key: string]: string } = {
  [Flows.ONBOARDING]: RouteNames.UPDATE_SUBSCRIPTION,
  [Flows.LOGIN]: RouteNames.LOGIN_UPDATE_SUBSCRIPTION,
  [Flows.MANAGE_SUBSCRIPTION]: RouteNames.MANAGE_SUBSCRIPTION_UPDATE_SUBSCRIPTION
}

const flowToUpgradeAccountUrlMap: { [key: string]: string } = {
  [Flows.ONBOARDING]: RouteNames.UPGRADE_ACCOUNT,
  [Flows.LOGIN]: RouteNames.LOGIN_UPGRADE_ACCOUNT
}

const flowToEbookPurchaseUrlMap: { [key: string]: string } = {
  [Flows.ONBOARDING]: RouteNames.EBOOK_PURCHASE,
  [Flows.LOGIN]: RouteNames.LOGIN_EBOOK_PURCHASE
}

// Where the MoR ebook checkout returns a paying buyer.
const flowToEbookPurchaseSuccessUrlMap: { [key: string]: string } = {
  [Flows.ONBOARDING]: RouteNames.EBOOK_PURCHASE_SUCCESS,
  [Flows.LOGIN]: RouteNames.LOGIN_EBOOK_PURCHASE_SUCCESS
}

const flowToDownloadUrlMap: { [key: string]: string } = {
  [Flows.ONBOARDING]: RouteNames.DOWNLOAD,
  [Flows.LOGIN]: RouteNames.LOGIN_DOWNLOAD,
  [Flows.MANAGE_SUBSCRIPTION]: RouteNames.MANAGE_SUBSCRIPTION_DOWNLOAD,
  [Flows.WELLHUB]: RouteNames.WELLHUB_DOWNLOAD,
  [Flows.USC]: RouteNames.USC_DOWNLOAD
}

const flowToGoToEventsMap: { [key: string]: { [key: string]: string } } = {
  [Flows.ONBOARDING]: GO_TO_ONBOARDING_EVENTS_MAP,
  [Flows.LOGIN]: GO_TO_LOGIN_EVENTS_MAP,
  [Flows.WELLHUB]: GO_TO_WELLHUB_EVENTS_MAP,
  [Flows.USC]: GO_TO_USC_EVENTS_MAP
}

const flowToCommitEventsMap: { [key: string]: { [key: string]: string } } = {
  [Flows.ONBOARDING]: COMMIT_ONBOARDING_EVENTS_MAP,
  [Flows.LOGIN]: COMMIT_LOGIN_EVENTS_MAP,
  [Flows.WELLHUB]: COMMIT_WELLHUB_EVENTS_MAP,
  [Flows.USC]: COMMIT_USC_EVENTS_MAP
}

const flowGoBackToEventsMap: { [key: string]: { [key: string]: string } } = {
  [Flows.ONBOARDING]: GO_BACK_TO_ONBOARDING_EVENTS_MAP,
  [Flows.LOGIN]: GO_BACK_TO_LOGIN_EVENTS_MAP,
  [Flows.WELLHUB]: GO_TO_WELLHUB_EVENTS_MAP,
  [Flows.USC]: GO_TO_USC_EVENTS_MAP
}

const flowToSkipEventsMap: { [key: string]: { [key: string]: string } } = {
  [Flows.ONBOARDING]: SKIP_ONBOARDING_EVENTS_MAP,
  [Flows.LOGIN]: {}
}
